import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiRefreshCw } from 'react-icons/fi';
import api from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';
import { isAdmin } from '../../../lib/roles';
import { useTheme } from '../../../context/ThemeContext';
import { HexLockup } from '../../../components/FieldCertLogo';
import { useTestSession } from '../../../hooks/useTestSession';
import StatTile from '../../../components/ui/StatTile';
import ComplianceRing, { getComplianceTone } from '../../../components/ui/ComplianceRing';
import { format } from 'date-fns';
import { getElementTypeLabel, sortSessionsByElementTypeOrder } from '../../../constants/AssetTestConfig';
import { formatDisplayShort, parseDateSafe } from '../../../lib/dateUtils';
import { isOwnInProgressSession } from '../../../lib/sessionOwnership';
import ActiveSessionsCard from './ActiveSessionsCard';
import RepairsRequiredCard from './RepairsRequiredCard';
import MobileDashboardStatsCard from './MobileDashboardStatsCard';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function useComplianceSummary() {
  const { data, isLoading, error } = useQuery(
    ['reports', 'compliance-summary'],
    () => api.get('/reports/compliance-summary').then((res) => res.data),
    { staleTime: 60 * 1000 }
  );
  const total = Number(data?.assetStatus?.total_assets) || 0;
  const current = Number(data?.assetStatus?.current_count) || 0;
  const overdue = Number(data?.assetStatus?.overdue_count) || 0;
  const rawAssetsDue = data?.assetStatus?.assets_due_count;
  const rawSitesDue = data?.assetStatus?.sites_due_count;
  const rawAssetsTested = data?.assetStatus?.assets_tested_this_month_count;
  const assetsDue = rawAssetsDue == null ? null : Number(rawAssetsDue);
  const sitesDue = rawSitesDue == null ? null : Number(rawSitesDue);
  const assetsTested = rawAssetsTested == null ? null : Number(rawAssetsTested);
  const percent = total > 0 ? Math.round((current / total) * 100) : 0;
  return { total, current, overdue, sitesDue, assetsDue, assetsTested, percent, isLoading, error };
}

async function getAllSitesWithCounts() {
  const limit = 100;
  const allSites = [];
  let page = 1;
  let hasNextPage = true;
  const safetyPageCap = 500;

  while (hasNextPage && page <= safetyPageCap) {
    const { data } = await api.get('/sites/with-counts', { params: { page, limit } });
    const rows = data?.sites ?? [];
    allSites.push(...rows);

    const pagination = data?.pagination;
    if (!pagination) break;
    hasNextPage = Boolean(pagination.hasNextPage);
    page += 1;
  }

  return allSites;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canDeleteSessions = isAdmin(user);
  const { effectiveTheme } = useTheme();
  const { queue, isSyncing } = useTestSession();
  const {
    total: totalAssets,
    sitesDue,
    assetsDue,
    assetsTested,
    isLoading: loadingCompliance,
    error: complianceError,
  } = useComplianceSummary();

  const { data: sites = [], isLoading: sitesLoading, error: sitesError } = useQuery(
    'sites-with-counts',
    () => getAllSitesWithCounts(),
    { staleTime: 60 * 1000 }
  );

  const sitesWithCounts = useMemo(() => (sites || []).map((site) => ({
    site,
    repairCount: Number(site.open_defect_count ?? 0),
  })), [sites]);

  const totalOutstandingRepairs = sitesWithCounts.reduce((sum, { repairCount }) => sum + repairCount, 0);

  const sitesNeedingAttention = useMemo(() =>
    sitesWithCounts
      .filter(({ repairCount }) => repairCount > 0)
      .sort((a, b) => b.repairCount - a.repairCount),
  [sitesWithCounts]);
  const repairsLoading = sitesLoading;

  const userId = user?.id ?? user?.userId;
  const { data: inProgressRaw, isError: inProgressError, isLoading: loadingSessions, error: inProgressErrorDetail } = useQuery(
    ['sessions-in-progress', userId],
    () => api.get('/sessions/in-progress').then((res) => res.data),
    { staleTime: 0, enabled: !!userId }
  );
  const deleteSessionMutation = useMutation(
    (sessionId) => api.delete(`/sessions/${sessionId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessions-in-progress']);
        toast.success('Session deleted.');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to delete session.');
      },
    }
  );

  const inProgressSessions = inProgressError ? [] : (inProgressRaw ?? []);
  const incompleteSessions = useMemo(() => {
    const rows = sortSessionsByElementTypeOrder([...inProgressSessions]);
    return rows.sort((a, b) => {
      const na = (a.site_name || '').toLowerCase();
      const nb = (b.site_name || '').toLowerCase();
      return na.localeCompare(nb, undefined, { numeric: true });
    });
  }, [inProgressSessions]);

  const totalSites = (sites || []).length;

  const displayName = (user?.firstName ?? user?.first_name)?.trim() || user?.email?.split('@')[0] || 'Technician';
  const greeting = getGreeting();
  const allSaved = queue.length === 0 && !isSyncing;
  const dashboardDataWarning = complianceError || sitesError || inProgressErrorDetail;
  const monthDueReady = assetsDue != null && assetsTested != null;
  const monthDueTotal = monthDueReady ? assetsDue + assetsTested : null;
  const monthDuePercent = !monthDueReady
    ? null
    : monthDueTotal > 0
      ? Math.round((assetsTested / monthDueTotal) * 100)
      : 0;
  const complianceTone = monthDueReady && monthDueTotal > 0
    ? getComplianceTone(monthDuePercent)
    : undefined;
  const dueCountsLoading = loadingCompliance;
  const complianceRingPercent = monthDueReady ? monthDuePercent : null;
  const complianceRingValue = monthDueReady ? assetsDue : null;

  const syncBadge = (
    <div
      className={`dashboard-sync-badge ${allSaved && !isSyncing ? 'dashboard-sync-badge-saved' : 'dashboard-sync-badge-unsaved'}`}
      title={allSaved && !isSyncing ? 'All data saved' : isSyncing ? 'Syncing…' : 'You have unsaved results'}
    >
      {isSyncing ? (
        <FiRefreshCw className="dashboard-stat-icon-spin" size={14} aria-hidden />
      ) : (
        <span className="dashboard-sync-badge-dot" aria-hidden />
      )}
      <span>
        {isSyncing ? 'Syncing…' : allSaved ? 'Synced' : 'Unsaved'}
      </span>
    </div>
  );

  return (
    <div className="page dashboard-modern">
      <header className="dashboard-header">
        {dashboardDataWarning ? (
          <p className="text-muted" style={{ marginBottom: '0.75rem' }}>
            Some dashboard data could not be loaded and may be incomplete.
          </p>
        ) : null}
        <div className="dashboard-mobile-top">
          <div className="dashboard-inpage-brand" aria-label="FieldCert">
            <HexLockup iconSize={28} wmSize="xs" gap={8} light={effectiveTheme === 'light'} />
          </div>
          {syncBadge}
        </div>
        <div className="dashboard-header-top">
          <div>
            <h1 className="dashboard-welcome">
              {greeting}, {displayName}
            </h1>
            <p className="dashboard-date">
              {format(new Date(), 'EEEE')} · {format(new Date(), 'd MMM yyyy')}
            </p>
          </div>
          <div className="dashboard-sync-badge-desktop">{syncBadge}</div>
        </div>
      </header>

      <div className="dashboard-kpi-row dashboard-desktop-only">
        <StatTile
          className="dashboard-stat-card"
          tone={complianceTone}
          value={loadingCompliance || !monthDueReady ? '…' : assetsDue}
          label="Due this month"
        />
        <StatTile
          className="dashboard-stat-card"
          value={sitesLoading ? '…' : totalSites}
          label="Total sites"
        />
        <StatTile
          className="dashboard-stat-card"
          value={loadingCompliance ? '…' : totalAssets}
          label="Total assets"
        />
        <StatTile
          className="dashboard-stat-card"
          tone="brand"
          value={repairsLoading ? '…' : totalOutstandingRepairs}
          label="Outstanding repairs"
        />
      </div>

      <div className="dashboard-mobile-hero dashboard-mobile-only">
        <ComplianceRing
          percent={complianceRingPercent}
          value={complianceRingValue}
          label="Due this month"
        />
        <MobileDashboardStatsCard
          sitesDue={sitesDue}
          repairsDue={totalOutstandingRepairs}
          isLoading={dueCountsLoading || repairsLoading}
        />
      </div>

      <div className="dashboard-grid dashboard-bottom-grid">
        <ActiveSessionsCard
          number="02"
          isLoading={loadingSessions}
          sessions={incompleteSessions.map((s) => {
            const siteName = s.site_name || 'Unnamed site';
            const typeLabel = getElementTypeLabel(s.element_type);
            return {
              id: s.id,
              siteId: s.site_id,
              siteName,
              meta: `${typeLabel} · ${
                s.start_time ? `Started ${formatDisplayShort(parseDateSafe(s.start_time))}` : '—'
              }`,
              canResume: isOwnInProgressSession(s, user),
              ownerLabel: `Started by ${s.user_name || 'Unknown'}`,
              resumeLabel: `Resume ${typeLabel} testing at ${siteName}`,
            };
          })}
          onResume={(session) => navigate('/tests', {
            state: { resumeSiteId: session.siteId, resumeSessionId: session.id },
          })}
          canDeleteSessions={canDeleteSessions}
          onDelete={(session) => {
            if (window.confirm(
              `Delete the session at ${session.siteName}? This permanently removes its recorded tests and cannot be undone.`
            )) {
              deleteSessionMutation.mutate(session.id);
            }
          }}
        />

        <RepairsRequiredCard
          number="03"
          isLoading={repairsLoading}
          sites={sitesNeedingAttention.map(({ site, repairCount }) => ({
            id: site.id,
            name: site.display_name ?? site.name ?? site.site_code ?? 'Unnamed site',
            location: [site.city, site.state].filter(Boolean).join(', ') || site.address || '',
            repairCount,
          }))}
          onRepair={(site) => navigate('/repairs', {
            state: { prefillSiteId: String(site.id) },
          })}
        />
      </div>
    </div>
  );
}
